import { iconSizes } from '@mds/tokens';
import {
  buttonGroupClasses,
  iconButtonClasses,
  svgIconClasses,
  toggleButtonClasses,
  toggleButtonGroupClasses,
  type Components,
  type CSSObject,
} from '@mui/material';
import { themeVars } from '../themeVars';
import { paletteColorNames, typographyStyles, type Theme } from './utils';

const buttonMinHeight = { small: 32, medium: 40, large: 48 };
const toggleButtonMinHeight = { small: 32, medium: 40, large: 48 };
const groupedToggleButtonSize = { small: 28, medium: 36, large: 44 };
const iconFontSize = {
  small: iconSizes.sm,
  medium: iconSizes.md,
  large: iconSizes.lg,
} satisfies Record<keyof typeof buttonMinHeight, number>;
const iconButtonIconFontSize = {
  small: iconSizes.md,
  medium: iconSizes.lg,
  large: iconSizes.xl,
} satisfies Record<keyof typeof buttonMinHeight, number>;
const iconButtonHeight = { small: 32, medium: 40, large: 48 } satisfies Record<keyof typeof buttonMinHeight, number>;
const iconButtonPadding = { small: 6, medium: 9, large: 12 } satisfies Record<keyof typeof buttonMinHeight, number>;
const iconOnlyToggleButtonSelector = `&:has(> .${svgIconClasses.root}:only-child)`;

function getIconFontSize(size: unknown) {
  return typeof size === 'string' && size in iconFontSize
    ? iconFontSize[size as keyof typeof iconFontSize]
    : iconFontSize.medium;
}

/**
 * Focus ring styles for button elements. This is different than .Mui-focusVisible from CSS baseline.
 */
const focusBorderStyles = (theme: Theme) => ({
  // 1px transparent width is required to keep dimensions during focus
  // TODO HDPI?
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'transparent',
  '&:focus-visible': {
    borderColor: themeVars(theme).palette.border.focused,
    // We should just be able to use this box shadow, unfortunately it renders poorly in
    // Chrome. The border radius is not smooth and the thickness varies based on element
    // it's applied to.
    boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
  },
});

export const MuiButton: Components<Theme>['MuiButton'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...typographyStyles(theme.typography.label2),
      padding: theme.spacing('10px', 1.5),
      transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'color'], {
        // Slow transition wil make the jump from black-on-white to white-on-black more jarring
        duration: theme.transitions.duration.shortest,
      }),
      // This is not very common but is probably a good default for most applications
      // https://html-css-tip-of-the-week.netlify.app/tip/max-width-fit-content/
      minWidth: 'fit-content',
      minHeight: buttonMinHeight.medium,
      ...focusBorderStyles(theme),
    }),
    startIcon: ({ ownerState, theme }) => ({
      marginLeft: 0,
      marginRight: theme.spacing(1),
      [`&& > .${svgIconClasses.root}`]: {
        fontSize: getIconFontSize(ownerState.size),
      },
    }),
    endIcon: ({ ownerState, theme }) => ({
      marginLeft: theme.spacing(1),
      marginRight: 0,
      [`&& > .${svgIconClasses.root}`]: {
        fontSize: getIconFontSize(ownerState.size),
      },
    }),
  },
  variants: [
    ...paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => {
        const paletteColor = themeVars(theme).palette[color];
        return {
          '--variant-containedColor': paletteColor.solidColor,
          '--variant-containedBg': paletteColor.solidBg,
          '--variant-outlinedColor': paletteColor.text,
          '--variant-outlinedBorder': paletteColor.solidBg,
          '--variant-textColor': paletteColor.text,
          '@media (hover: hover)': {
            '&:hover': {
              '--variant-containedBg': paletteColor.solidHoverBg,
              '--variant-outlinedColor': paletteColor.solidColor,
              '--variant-outlinedBg': paletteColor.solidHoverBg,
              '--variant-outlinedBorder': paletteColor.solidHoverBg,
              '--variant-textColor': paletteColor.solidColor,
              '--variant-textBg': paletteColor.solidHoverBg,
            },
          },
          '&:active': {
            '--variant-containedBg': paletteColor.solidPressedBg,
            '--variant-outlinedColor': paletteColor.solidColor,
            '--variant-outlinedBg': paletteColor.solidPressedBg,
            '--variant-outlinedBorder': paletteColor.solidPressedBg,
            '--variant-textColor': paletteColor.solidColor,
            '--variant-textBg': paletteColor.solidPressedBg,
            '--variant-textBorder': paletteColor.solidBg,
          },
        };
      },
    })),
    {
      props: { variant: 'outlined' },
      style: {
        borderColor: 'var(--variant-outlinedBorder)',
      },
    },
    {
      props: { color: 'neutral', variant: 'text' },
      style: ({ theme }) => {
        return {
          '--variant-textColor': themeVars(theme).palette.text.primary,
          textDecoration: 'underline',
          '@media (hover: hover)': {
            '&:hover': {
              textDecoration: 'underline',
              '--variant-textBg': themeVars(theme).palette.action.hover,
            },
          },
          '&:active': {
            '--variant-textBg': themeVars(theme).palette.action.pressed,
          },
        };
      },
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.label3),
        padding: theme.spacing('7px', 1.5),
        minHeight: buttonMinHeight.small,
      }),
    },
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.label2),
        padding: theme.spacing('13px', 2),
        minHeight: buttonMinHeight.large,
      }),
    },
  ],
};

export const MuiButtonGroup: Components<Theme>['MuiButtonGroup'] = {
  styleOverrides: {
    root: ({ ownerState, theme }) => {
      const groupedFocusStyles = focusBorderStyles(theme);
      const isOutlined = ownerState.variant === 'outlined';
      const isVertical = ownerState.orientation === 'vertical';

      return {
        gap: isOutlined ? 0 : 1,
        [`& .${buttonGroupClasses.grouped}`]: isOutlined
          ? { '&:focus-visible': groupedFocusStyles['&:focus-visible'] }
          : groupedFocusStyles,
        ...(isOutlined && {
          [`&& .${buttonGroupClasses.firstButton}, && .${buttonGroupClasses.middleButton}`]: {
            ...(isVertical ? { borderBottomColor: 'transparent' } : { borderRightColor: 'transparent' }),
            '&:hover:not(:focus-visible)': isVertical
              ? { borderBottomColor: 'transparent' }
              : { borderRightColor: 'transparent' },
          },
          [`&& .${buttonGroupClasses.middleButton}, && .${buttonGroupClasses.lastButton}`]: isVertical
            ? { marginTop: -1 }
            : { marginLeft: -1 },
        }),
      };
    },
  },
};

export const MuiToggleButtonGroup: Components<Theme>['MuiToggleButtonGroup'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      border: `1px solid ${themeVars(theme).palette.border.subtle}`,
      height: toggleButtonMinHeight.medium,
      padding: 0,
      [`& .${toggleButtonGroupClasses.grouped}`]: {
        '--mds-toggle-button-min-height': `${groupedToggleButtonSize.medium}px`,
        border: 0,
        [`&.${toggleButtonGroupClasses.disabled}`]: {
          border: 0,
        },
      },
      [`& .${toggleButtonGroupClasses.grouped}:has(> .${svgIconClasses.root}:only-child)`]: {
        '--mds-toggle-button-size': `${groupedToggleButtonSize.medium}px`,
        paddingInline: 0,
      },
      [`& .${toggleButtonGroupClasses.firstButton},
        & .${toggleButtonGroupClasses.middleButton},
        & .${toggleButtonGroupClasses.lastButton}`]: {
        borderRadius: theme.shape.borderRadius,
        margin: 1,
      },
    }),
  },
  variants: [
    {
      props: { size: 'small' },
      style: {
        height: toggleButtonMinHeight.small,
        [`& .${toggleButtonGroupClasses.grouped}`]: {
          '--mds-toggle-button-min-height': `${groupedToggleButtonSize.small}px`,
        },
        [`& .${toggleButtonGroupClasses.grouped}:has(> .${svgIconClasses.root}:only-child)`]: {
          '--mds-toggle-button-size': `${groupedToggleButtonSize.small}px`,
        },
      },
    },
    {
      props: { size: 'large' },
      style: {
        height: toggleButtonMinHeight.large,
        [`& .${toggleButtonGroupClasses.grouped}`]: {
          '--mds-toggle-button-min-height': `${groupedToggleButtonSize.large}px`,
        },
        [`& .${toggleButtonGroupClasses.grouped}:has(> .${svgIconClasses.root}:only-child)`]: {
          '--mds-toggle-button-size': `${groupedToggleButtonSize.large}px`,
        },
      },
    },
  ],
};

export const MuiToggleButton: Components<Theme>['MuiToggleButton'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      ...focusBorderStyles(theme),
      ...typographyStyles(theme.typography.label2),
      paddingBlock: 0,
      paddingInline: theme.spacing(1),
      minHeight: `var(--mds-toggle-button-min-height, ${toggleButtonMinHeight.medium}px)`,

      [iconOnlyToggleButtonSelector]: {
        width: `var(--mds-toggle-button-size, ${toggleButtonMinHeight.medium}px)`,
        paddingInline: 0,
      },

      transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'color'], {
        // Slow transition wil make the jump from black-on-white to white-on-black more jarring
        duration: theme.transitions.duration.shortest,
      }),
      color: themeVars(theme).palette.text.secondary,
      backgroundColor: 'transparent',

      '&:hover': {
        color: 'var(--mds-ToggleButton-hoverColor)',
        backgroundColor: 'var(--mds-ToggleButton-hoverBg)',
      },

      [`&.${toggleButtonClasses.selected}, &.${toggleButtonClasses.selected}:hover`]: {
        color: 'var(--mds-ToggleButton-selectedColor)',
        backgroundColor: 'var(--mds-ToggleButton-selectedBg)',
      },

      [`& :where(.${svgIconClasses.fontSizeMedium})`]: {
        fontSize: iconFontSize.medium,
      },
    }),
  },
  variants: [
    ...paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => {
        const paletteColor = themeVars(theme).palette[color];
        return {
          '--mds-ToggleButton-selectedColor': paletteColor.solidColor,
          '--mds-ToggleButton-selectedBg': paletteColor.solidBg,
          '--mds-ToggleButton-hoverColor': paletteColor.solidColor,
          '--mds-ToggleButton-hoverBg': paletteColor.solidHoverBg,
        };
      },
    })),
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.label3),
        paddingBlock: 0,
        paddingInline: theme.spacing(0.75),
        minHeight: `var(--mds-toggle-button-min-height, ${toggleButtonMinHeight.small}px)`,

        [iconOnlyToggleButtonSelector]: {
          width: `var(--mds-toggle-button-size, ${toggleButtonMinHeight.small}px)`,
          paddingInline: 0,
        },

        [`& :where(.${svgIconClasses.fontSizeMedium})`]: {
          fontSize: iconFontSize.small,
        },
      }),
    },
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        ...typographyStyles(theme.typography.label2),
        paddingBlock: 0,
        paddingInline: theme.spacing(2),
        minHeight: `var(--mds-toggle-button-min-height, ${toggleButtonMinHeight.large}px)`,

        [iconOnlyToggleButtonSelector]: {
          width: `var(--mds-toggle-button-size, ${toggleButtonMinHeight.large}px)`,
          paddingInline: 0,
        },

        [`& :where(.${svgIconClasses.fontSizeMedium})`]: {
          fontSize: iconFontSize.large,
        },
      }),
    },
  ],
};

export const MuiIconButton: Components<Theme>['MuiIconButton'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: theme.shape.borderRadius,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'var(--mds-icon-button-box-sizing, border-box)' as CSSObject['boxSizing'],
      height: `var(--mds-icon-button-height, ${iconButtonHeight.medium}px)`,
      width: `var(--mds-icon-button-width, ${iconButtonHeight.medium}px)`,
      minWidth: `var(--mds-icon-button-min-width, ${iconButtonHeight.medium}px)`,
      minHeight: `var(--mds-icon-button-min-height, ${iconButtonHeight.medium}px)`,
      padding: `var(--mds-icon-button-padding, ${iconButtonPadding.medium}px)`,
      color: 'var(--variant-textColor)',
      backgroundColor: 'transparent',
      transition: theme.transitions.create(['background-color', 'box-shadow', 'border-color', 'color'], {
        duration: theme.transitions.duration.shortest,
      }),
      ...focusBorderStyles(theme),
      [`& :where(.${svgIconClasses.root})`]: {
        fontSize: iconButtonIconFontSize.medium,
      },
      '@media (hover: hover)': {
        '&:hover': {
          color: 'var(--variant-textColor)',
          backgroundColor: 'var(--variant-textBg)',
        },
      },
      '&:active': {
        color: 'var(--variant-textColor)',
        backgroundColor: 'var(--variant-textBg)',
        borderColor: 'var(--variant-textBorder, transparent)',
      },
      [`&.${iconButtonClasses.disabled}`]: {
        color: themeVars(theme).palette.action.disabled,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
      [`&.${iconButtonClasses.loading}`]: {
        color: 'transparent',
      },
      [`&.${iconButtonClasses.edgeEnd}`]: {
        marginRight: 'var(--mds-icon-button-edge-end-margin, -12px)',
      },
      [`&.${iconButtonClasses.edgeStart}`]: {
        marginLeft: 'var(--mds-icon-button-edge-start-margin, -12px)',
      },
    }),
  },
  variants: [
    ...paletteColorNames.map((color) => ({
      props: { color },
      style: ({ theme }: { theme: Theme }) => {
        const paletteColor = themeVars(theme).palette[color];
        return {
          '--variant-containedColor': paletteColor.solidColor,
          '--variant-containedBg': paletteColor.solidBg,
          '--variant-outlinedColor': paletteColor.text,
          '--variant-outlinedBorder': paletteColor.solidBg,
          '--variant-textColor': paletteColor.text,
          '@media (hover: hover)': {
            '&:hover': {
              '--variant-containedBg': paletteColor.solidHoverBg,
              '--variant-outlinedColor': paletteColor.solidColor,
              '--variant-outlinedBg': paletteColor.solidHoverBg,
              '--variant-outlinedBorder': paletteColor.solidHoverBg,
              '--variant-textColor': paletteColor.solidColor,
              '--variant-textBg': paletteColor.solidHoverBg,
            },
          },
          '&:active': {
            '--variant-containedBg': paletteColor.solidPressedBg,
            '--variant-outlinedColor': paletteColor.solidColor,
            '--variant-outlinedBg': paletteColor.solidPressedBg,
            '--variant-outlinedBorder': paletteColor.solidPressedBg,
            '--variant-textColor': paletteColor.solidColor,
            '--variant-textBg': paletteColor.solidPressedBg,
            '--variant-textBorder': paletteColor.solidBg,
          },
        };
      },
    })),
    {
      props: { color: 'default' },
      style: ({ theme }) => {
        const palette = themeVars(theme).palette;

        return {
          '--variant-containedColor': palette.text.contrast,
          '--variant-containedBg': palette.text.primary,
          '--variant-outlinedColor': palette.text.primary,
          '--variant-outlinedBorder': palette.border.strong,
          '--variant-textColor': palette.text.primary,
          '@media (hover: hover)': {
            '&:hover': {
              '--variant-containedBg': palette.text.secondary,
              '--variant-outlinedBg': palette.action.hover,
              '--variant-textBg': palette.action.hover,
            },
          },
          '&:active': {
            '--variant-containedBg': palette.text.secondary,
            '--variant-outlinedBg': palette.action.pressed,
            '--variant-textBg': palette.action.pressed,
            '--variant-textBorder': palette.border.strong,
          },
        };
      },
    },
    {
      props: { color: 'neutral' },
      style: ({ theme }) => ({
        '--variant-textColor': themeVars(theme).palette.text.primary,
        '@media (hover: hover)': {
          '&:hover': {
            '--variant-textBg': themeVars(theme).palette.action.hover,
          },
        },
        '&:active': {
          '--variant-textBg': themeVars(theme).palette.action.pressed,
          '--variant-textBorder': themeVars(theme).palette.border.strong,
        },
      }),
    },
    {
      props: { variant: 'contained' },
      style: {
        color: 'var(--variant-containedColor)',
        backgroundColor: 'var(--variant-containedBg)',
        '@media (hover: hover)': {
          '&:hover': {
            backgroundColor: 'var(--variant-containedBg)',
          },
        },
        '&:active': {
          backgroundColor: 'var(--variant-containedBg)',
        },
      },
    },
    {
      props: { variant: 'outlined' },
      style: {
        color: 'var(--variant-outlinedColor)',
        backgroundColor: 'transparent',
        borderColor: 'var(--variant-outlinedBorder)',
        '@media (hover: hover)': {
          '&:hover': {
            color: 'var(--variant-outlinedColor)',
            backgroundColor: 'var(--variant-outlinedBg)',
            borderColor: 'var(--variant-outlinedBorder)',
          },
        },
        '&:active': {
          color: 'var(--variant-outlinedColor)',
          backgroundColor: 'var(--variant-outlinedBg)',
          borderColor: 'var(--variant-outlinedBorder)',
        },
      },
    },
    {
      props: { variant: 'text' },
      style: {
        color: 'var(--variant-textColor)',
        backgroundColor: 'transparent',
        borderColor: 'var(--variant-textBorder, transparent)',
        '@media (hover: hover)': {
          '&:hover': {
            color: 'var(--variant-textColor)',
            backgroundColor: 'var(--variant-textBg)',
          },
        },
        '&:active': {
          color: 'var(--variant-textColor)',
          backgroundColor: 'var(--variant-textBg)',
          borderColor: 'var(--variant-textBorder, transparent)',
        },
      },
    },
    {
      props: { size: 'small' },
      style: {
        height: `var(--mds-icon-button-height, ${iconButtonHeight.small}px)`,
        width: `var(--mds-icon-button-width, ${iconButtonHeight.small}px)`,
        minWidth: `var(--mds-icon-button-min-width, ${iconButtonHeight.small}px)`,
        minHeight: `var(--mds-icon-button-min-height, ${iconButtonHeight.small}px)`,
        padding: `var(--mds-icon-button-padding, ${iconButtonPadding.small}px)`,
        [`&.${iconButtonClasses.edgeEnd}`]: {
          marginRight: 'var(--mds-icon-button-edge-end-margin, -3px)',
        },
        [`&.${iconButtonClasses.edgeStart}`]: {
          marginLeft: 'var(--mds-icon-button-edge-start-margin, -3px)',
        },
        [`& :where(.${svgIconClasses.root})`]: {
          fontSize: iconButtonIconFontSize.small,
        },
      },
    },
    {
      props: { size: 'large' },
      style: {
        height: `var(--mds-icon-button-height, ${iconButtonHeight.large}px)`,
        width: `var(--mds-icon-button-width, ${iconButtonHeight.large}px)`,
        minWidth: `var(--mds-icon-button-min-width, ${iconButtonHeight.large}px)`,
        minHeight: `var(--mds-icon-button-min-height, ${iconButtonHeight.large}px)`,
        padding: `var(--mds-icon-button-padding, ${iconButtonPadding.large}px)`,
        [`& :where(.${svgIconClasses.root})`]: {
          fontSize: iconButtonIconFontSize.large,
        },
      },
    },
  ],
};
